// src/pages/client/ClientJobsPage.jsx
// Client Job Loader - works with both URL params and subdomain routing
//
// Access patterns:
// - invenire.app/client/supabase/jobs → tenantKey from URL param
// - supabase.invenire.app/admin/jobs → tenantKey from subdomain (prop)

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { trackPageView, trackJobCreated } from "../../utils/analytics";

const API_BASE = process.env.REACT_APP_API_BASE || "https://api.invenire.app";

export default function ClientJobsPage({ tenantKeyFromSubdomain }) {
  // Get tenantKey from URL params OR from subdomain prop
  const { tenantKey: tenantKeyFromUrl } = useParams();
  const tenantKey = tenantKeyFromSubdomain || tenantKeyFromUrl;
  
  // State
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Form state
  const [inputMode, setInputMode] = useState("url"); // "url" or "paste"
  const [url, setUrl] = useState("");
  const [pastedText, setPastedText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  
  // Result state
  const [result, setResult] = useState(null);
  const [resultError, setResultError] = useState(null);
  
  // Jobs list
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  
  // Determine if we're on subdomain (affects link paths)
  const isSubdomain = Boolean(tenantKeyFromSubdomain);
  
  // Generate correct paths based on routing mode
  const getAdminPath = () => isSubdomain ? "/admin" : `/client/${tenantKey}`;
  
  // Apply tenant branding (computed early for use in all renders, memoized)
  const branding = useMemo(() => tenant?.branding || {}, [tenant?.branding]);
  const primaryColor = branding.primaryColor || "#3ECF8E";
  const companyName = branding.companyName || tenant?.name || tenantKey;
  
  // Get backgroundColor from branding (matches ClientConfigPage approach)
  const backgroundColor = branding.backgroundColor || branding.background_color || '#0d1117';
  
  // Calculate if background is light or dark for text color
  const isLightBackground = useMemo(() => {
    const hex = backgroundColor.replace('#', '');
    if (hex.length === 6) {
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5;
    }
    return false;
  }, [backgroundColor]);
  
  // Dynamic styles based on tenant branding - ALL styles here, no static styles
  const dynamicStyles = useMemo(() => {
    const textColor = branding.textColor || (isLightBackground ? '#111827' : '#ffffff');
    const textSecondary = isLightBackground ? '#4b5563' : '#9ca3af';
    const cardBg = isLightBackground ? '#ffffff' : 'rgba(255,255,255,0.03)';
    const cardBorder = isLightBackground ? '#e5e7eb' : 'rgba(255,255,255,0.1)';
    const inputBg = isLightBackground ? '#f9fafb' : 'rgba(255,255,255,0.05)';
    const inputBorder = isLightBackground ? '#d1d5db' : 'rgba(255,255,255,0.1)';
    const buttonBg = branding.buttonPrimaryBg || primaryColor;
    const buttonText = branding.buttonTextColor || '#ffffff';
    const hoverBg = isLightBackground ? '#f3f4f6' : 'rgba(255,255,255,0.05)';
    
    return {
      // Core layout
      container: { minHeight: "100vh", backgroundColor: backgroundColor, color: textColor, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
      header: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 40px", borderBottom: `1px solid ${cardBorder}` },
      title: { fontSize: 28, fontWeight: 700, margin: 0, color: primaryColor },
      subtitle: { fontSize: 14, color: textSecondary },
      backButton: { padding: "8px 16px", background: isLightBackground ? '#f3f4f6' : "rgba(255,255,255,0.1)", color: textColor, textDecoration: "none", borderRadius: 6, fontSize: 14, border: "none", cursor: "pointer" },
      content: { maxWidth: 1000, margin: "0 auto", padding: "40px 20px" },
      
      // Loading & Error states
      loading: { display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", fontSize: 18, color: textSecondary },
      errorTitle: { fontSize: 24, marginBottom: 10, color: textColor },
      errorText: { color: "#ef4444", marginBottom: 20 },
      backLink: { color: primaryColor, textDecoration: "none" },
      
      // Layout
      twoColumn: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40 },
      formSection: { background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 12, padding: 24 },
      resultSection: { background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 12, padding: 24 },
      
      // Section titles
      sectionTitle: { fontSize: 20, marginBottom: 8, marginTop: 0, color: textColor, fontWeight: 600 },
      sectionText: { color: textSecondary, fontSize: 14, marginBottom: 24 },
      
      // Form elements
      formGroup: { marginBottom: 20 },
      label: { display: "block", fontSize: 14, color: textSecondary, marginBottom: 8 },
      input: { width: "100%", padding: "12px 16px", background: inputBg, border: `1px solid ${inputBorder}`, borderRadius: 6, color: textColor, fontSize: 14, boxSizing: "border-box" },
      textarea: { width: "100%", padding: "12px 16px", background: inputBg, border: `1px solid ${inputBorder}`, borderRadius: 6, color: textColor, fontSize: 14, minHeight: 120, resize: "vertical", boxSizing: "border-box" },
      hint: { fontSize: 12, color: textSecondary, marginTop: 6 },
      
      // Mode buttons
      modeButtons: { display: "flex", gap: 12, marginBottom: 24 },
      modeButton: { flex: 1, padding: "12px 16px", background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 8, color: textColor, fontSize: 14, cursor: "pointer", textAlign: "center" },
      modeButtonActive: { background: primaryColor, borderColor: primaryColor, color: "#ffffff" },
      
      // Submit button
      submitBtn: { width: "100%", padding: "14px 24px", backgroundColor: buttonBg, color: buttonText, border: "none", borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: "pointer", marginTop: 8 },
      submitBtnDisabled: { opacity: 0.5, cursor: "not-allowed" },
      refreshButton: { padding: "8px 14px", background: "transparent", border: `1px solid ${cardBorder}`, borderRadius: 6, color: textSecondary, cursor: "pointer", fontSize: 13 },
      
      // Jobs list
      jobsList: { display: "flex", flexDirection: "column", gap: 12 },
      jobCard: { background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 8, padding: 16, cursor: "pointer", transition: "border-color 0.2s" },
      jobTitle: { fontWeight: 600, marginBottom: 4, color: textColor },
      jobCompany: { fontSize: 13, color: textSecondary, marginBottom: 8 },
      jobMeta: { display: "flex", gap: 16, fontSize: 12, color: textSecondary },
      
      // Empty state
      emptyState: { textAlign: "center", padding: 48, color: textSecondary },
      
      // Status messages
      successMessage: { marginTop: 16, padding: 16, background: "rgba(34, 197, 94, 0.1)", border: "1px solid rgba(34, 197, 94, 0.3)", borderRadius: 8, color: "#22c55e" },
      errorMessage: { marginTop: 16, padding: 16, background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: 8, color: "#ef4444" },
      
      // Card base
      card: { background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 12, padding: 24 },
      button: { padding: "12px 24px", background: buttonBg, color: buttonText, border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" },
      
      // Expose values for inline use
      textColor, textSecondary, cardBg, cardBorder, inputBg, inputBorder, buttonBg, buttonText, isLightBackground, primaryColor, hoverBg
    };
  }, [branding, primaryColor, backgroundColor, isLightBackground]);

  // Track page view
  useEffect(() => {
    const pagePath = isSubdomain ? '/admin/jobs' : '/client/[key]/jobs';
    trackPageView(pagePath, 'Client Jobs');
  }, [isSubdomain]);
  
  // Load tenant
  useEffect(() => {
    if (!tenantKey) {
      setError("No client specified");
      setLoading(false);
      return;
    }
    
    async function loadData() {
      try {
        const tenantResponse = await fetch(`${API_BASE}/tenants/${tenantKey}`);
        if (!tenantResponse.ok) {
          throw new Error(`Client "${tenantKey}" not found`);
        }
        const tenantData = await tenantResponse.json();
        setTenant(tenantData);
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    
    loadData();
  }, [tenantKey]);
  
  // Load jobs when tenant is loaded
  const loadJobs = useCallback(async () => {
    if (!tenant?.id) return;
    
    setLoadingJobs(true);
    try {
      const response = await fetch(`${API_BASE}/jobs/tenant/${tenant.id}`);
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
      }
    } catch (err) {
      console.error("Failed to load jobs:", err);
    } finally {
      setLoadingJobs(false);
    }
  }, [tenant?.id]);

  useEffect(() => {
    if (tenant?.id) {
      loadJobs();
    }
  }, [tenant?.id, loadJobs]);
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (inputMode === "url" && !url.trim()) {
      setResultError("Please enter a job URL");
      return;
    }
    
    if (inputMode === "paste" && !pastedText.trim()) {
      setResultError("Please paste the job description");
      return;
    }
    
    setSubmitting(true);
    setResult(null);
    setResultError(null);
    setStatusMessage(inputMode === "url" ? "Fetching job posting..." : "Parsing job description...");
    
    try {
      const endpoint = inputMode === "url" ? "/jobs/parse" : "/jobs/parse-text";
      const body = inputMode === "url" 
        ? { url: url.trim(), tenant_id: tenant.id }
        : { text: pastedText.trim(), tenant_id: tenant.id };
      
      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setResult(data);
        setUrl("");
        setPastedText("");
        setStatusMessage("");
        loadJobs();
        
        // Track job creation
        trackJobCreated(data.job_id, tenant.id, inputMode);
      } else {
        setResultError(data.error || "Failed to parse job");
        setStatusMessage("");
      }
    } catch (err) {
      setResultError(err.message);
      setStatusMessage("");
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleDeleteJob = async (jobId) => {
    if (!window.confirm("Are you sure you want to delete this job?")) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/jobs/${jobId}`, {
        method: "DELETE"
      });
      
      if (response.ok) {
        loadJobs();
      }
    } catch (err) {
      console.error("Failed to delete job:", err);
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div style={dynamicStyles.container}>
        <div style={dynamicStyles.loading}>Loading...</div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div style={dynamicStyles.container}>
        <div style={dynamicStyles.header}>
          <h1 style={dynamicStyles.title}>Invenire</h1>
          <span style={dynamicStyles.subtitle}>Error</span>
        </div>
        <div style={dynamicStyles.content}>
          <h2 style={{ ...dynamicStyles.errorTitle, color: dynamicStyles.textColor }}>Client Not Found</h2>
          <p style={dynamicStyles.errorText}>{error}</p>
          <Link to="/" style={{ ...dynamicStyles.backLink, color: primaryColor }}>← Back to Home</Link>
        </div>
      </div>
    );
  }
  
  return (
    <div style={dynamicStyles.container}>
      {/* Header */}
      <div style={dynamicStyles.header}>
        <div>
          <h1 style={dynamicStyles.title}>{companyName}</h1>
          <span style={dynamicStyles.subtitle}>Job Loader</span>
        </div>
        <Link to={getAdminPath()} style={dynamicStyles.backButton}>← Admin Home</Link>
      </div>
      
      {/* Main Content */}
      <div style={dynamicStyles.content}>
        <div style={dynamicStyles.twoColumn}>
          {/* Form Section */}
          <div style={{ ...dynamicStyles.formSection, background: dynamicStyles.cardBg, border: `1px solid ${dynamicStyles.cardBorder}` }}>
            <h2 style={dynamicStyles.sectionTitle}>Import Job</h2>
            <p style={dynamicStyles.sectionText}>
              Import from URL or paste the job description directly.
            </p>
            
            {/* Mode Toggle */}
            <div style={dynamicStyles.modeToggle}>
              <button
                type="button"
                onClick={() => setInputMode("url")}
                style={{
                  ...dynamicStyles.modeButton,
                  ...(inputMode === "url" ? dynamicStyles.modeButtonActive : {})
                }}
              >
                From URL
              </button>
              <button
                type="button"
                onClick={() => setInputMode("paste")}
                style={{
                  ...dynamicStyles.modeButton,
                  ...(inputMode === "paste" ? dynamicStyles.modeButtonActive : {})
                }}
              >
                Paste Text
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              {/* URL Input */}
              {inputMode === "url" && (
                <div style={dynamicStyles.formGroup}>
                  <label style={dynamicStyles.label}>Job Posting URL</label>
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://careers.company.com/job/123"
                    style={dynamicStyles.input}
                    disabled={submitting}
                  />
                  <p style={dynamicStyles.hint}>
                    Note: LinkedIn, Workday, and some other sites may not work. Use "Paste Text" instead.
                  </p>
                </div>
              )}
              
              {/* Paste Text Input */}
              {inputMode === "paste" && (
                <div style={dynamicStyles.formGroup}>
                  <label style={dynamicStyles.label}>Job Description</label>
                  <textarea
                    value={pastedText}
                    onChange={(e) => setPastedText(e.target.value)}
                    placeholder="Paste the full job description here..."
                    style={dynamicStyles.textarea}
                    disabled={submitting}
                    rows={8}
                  />
                  <p style={dynamicStyles.hint}>
                    Copy the job title, description, requirements, etc. from the job posting page.
                  </p>
                </div>
              )}
              
              {/* Error Message */}
              {resultError && (
                <div style={dynamicStyles.errorBox}>
                  {resultError}
                </div>
              )}
              
              {/* Status Message */}
              {statusMessage && (
                <div style={dynamicStyles.statusBox}>
                  {statusMessage}
                </div>
              )}
              
              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting || (inputMode === "url" ? !url.trim() : !pastedText.trim())}
                style={{
                  width: "100%",
                  padding: "14px 24px",
                  backgroundColor: primaryColor,
                  color: "#ffffff",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 16,
                  fontWeight: 600,
                  marginTop: 8,
                  cursor: (submitting || (inputMode === "url" ? !url.trim() : !pastedText.trim())) ? "not-allowed" : "pointer",
                  opacity: (submitting || (inputMode === "url" ? !url.trim() : !pastedText.trim())) ? 0.6 : 1
                }}
              >
                {submitting ? "Processing..." : "Import Job"}
              </button>
            </form>
            
            {/* Result Preview */}
            {result && (
              <div style={dynamicStyles.resultPreview}>
                <div style={dynamicStyles.resultHeader}>
                  <span style={{ color: primaryColor }}>✓ Job Created</span>
                  <span style={dynamicStyles.jobId}>ID: {result.job_id}</span>
                </div>
                <h3 style={dynamicStyles.resultTitle}>{result.title}</h3>
                <p style={dynamicStyles.resultMeta}>
                  {result.company_name} • {result.location}
                </p>
                {result.description && (
                  <p style={dynamicStyles.resultDescription}>
                    {result.description.slice(0, 200)}...
                  </p>
                )}
              </div>
            )}
          </div>
          
          {/* Jobs List Section */}
          <div style={dynamicStyles.resultSection}>
            <div style={dynamicStyles.listHeader}>
              <h2 style={dynamicStyles.sectionTitle}>Your Jobs</h2>
              <button 
                onClick={loadJobs} 
                disabled={loadingJobs}
                style={dynamicStyles.refreshButton}
              >
                {loadingJobs ? "..." : "↻ Refresh"}
              </button>
            </div>
            
            {jobs.length === 0 ? (
              <p style={dynamicStyles.resultPlaceholder}>
                No jobs imported yet. Import your first job above.
              </p>
            ) : (
              <div style={dynamicStyles.jobsList}>
                {jobs.map(job => (
                  <div key={job.id} style={dynamicStyles.jobCard}>
                    <div style={dynamicStyles.jobInfo}>
                      <div style={dynamicStyles.jobTitle}>
                        {job.title}
                        {job.source_url && (
                          <a 
                            href={job.source_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={dynamicStyles.sourceLink}
                          >
                            ↗
                          </a>
                        )}
                      </div>
                      <div style={dynamicStyles.jobMeta}>
                        {job.company_name} • {job.location}
                      </div>
                      <div style={dynamicStyles.jobFooter}>
                        ID: {job.id} • {new Date(job.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteJob(job.id)}
                      style={dynamicStyles.deleteButton}
                      title="Delete job"
                    >
                      🗑
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

